import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CALENDLY_URL = "https://calendly.com/call24x7-info/30min";

interface CalendlyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CalendlyModal({ open, onOpenChange }: CalendlyModalProps) {
  const { t } = useTranslation();
  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID;

  // Load Calendly embed script once
  useEffect(() => {
    if (document.querySelector('script[src*="calendly.com/assets/external/widget.js"]')) return;
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (open) {
      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "calendly_modal_opened", {
          event_category: "engagement",
          event_label: "Book a Meeting Modal",
        });
      }
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("track", "Schedule", {
          content_name: "Calendly Meeting",
        });
      }
    }
  }, [open]);

  // Listen for Calendly scheduling complete event via postMessage
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Calendly sends messages with event.data.event
      const isCalendlyEvent =
        event.origin === "https://calendly.com" &&
        event.data?.event === "calendly.event_scheduled";

      if (isCalendlyEvent) {
        console.log("Calendly: Meeting scheduled - firing conversion");

        // Virtual pageview for Google Ads
        if (window.gtag && googleAdsId) {
          window.gtag("event", "page_view", {
            page_path: "/thank-you-booking",
            page_title: "Thank You - Meeting Booked",
            page_location: window.location.origin + "/thank-you-booking",
          });
          window.gtag("event", "conversion", {
            send_to: `${googleAdsId}/booking_complete`,
            value: 1.0,
            currency: "USD",
          });
          console.log("Google Ads: Booking conversion fired");
        }

        if (window.fbq) {
          window.fbq("track", "Schedule", {
            content_name: "Calendly Meeting Booked",
          });
        }

        toast.success(t("thankYou.title", "Meeting booked successfully! 🎉"));
      }
    };

    // Always listen - Calendly sends postMessage even from embedded iframes
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [googleAdsId, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] md:max-w-[800px] h-[85vh] max-h-[700px] p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>{t("header.bookMeeting")}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 h-full min-h-0">
          <iframe
            src={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=1a1a1a&primary_color=3b82f6`}
            width="100%"
            height="100%"
            frameBorder="0"
            title="Schedule a meeting"
            className="min-h-[550px]"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { CALENDLY_URL };
